import { useEffect, useState, useContext, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useBreakpoints } from "@arise-biodiversity/components";
import { useKingdomData } from '../../hooks/useKingdomData';
import TargetlistGrid from "./target_list_grid";
import TargetlistSearch from "./target_list_search";
import TargetlistGridHeader from "./target_list_grid_header";
import SpeciesIdCard from "./species-id-card";
import {
  isEqualOrMoreSpecific,
  specificityList,
} from "../../model/specificity";
import { TableStateContext, columnKeys } from "../../model/table_state_context";
import { ThemeContext } from "../../themes/ThemeContext";
import Breadcrumbs from "./breadcrumbs";
import KingdomsCharts from "./kingdoms-charts";
import MobileKingdomCharts from "./kingdom-charts-mobile";
import TableChart from "./table-chart";

// contains alls the other components that make up the target list page
// also deals with url parameters etc.
export default function TargetlistContainer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [urlSearchString, setUrlSearchString] = useState(undefined);
  const [urlShowFrom, setUrlShowFrom] = useState(undefined);
  const [urlSelectedIndex, setUrlSelectedIndex] = useState(0);
  const [hideColumns, setHideColumns] = useState([]);
  const { filterBySearchTerm, numberOfItems, table } = useContext(TableStateContext);
  const [carouselItems, setCarouselItems] = useState([]);
  const [groupBy, setGroupBy] = useState("species"); // Initialize to default value 'species'
  const { theme } = useContext(ThemeContext);
  const [currentTaxon, setCurrentTaxon] = useState(null);
  const { isBelow } = useBreakpoints();
  const isMobile = isBelow("--breakpoint-md");
  const { kingdomData, loading } = useKingdomData();
  const mobileColumns = ['genus', 'species', 'speciesStatus'];

  useEffect(() => {
    if (searchParams.has("q")) {
      setUrlSearchString(searchParams.get("q"));
    } else {
      setUrlSearchString(undefined);
    }
    if (searchParams.has("showfrom")) {
      setUrlShowFrom(searchParams.get("showfrom"));
    } else {
      setUrlShowFrom(undefined);
    }
    if (searchParams.has("selected")) {
      setUrlSelectedIndex(Number(searchParams.get("selected")));
    } else {
      setUrlSelectedIndex(0);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!urlSearchString || !urlShowFrom) {
      // one already set but not the other, do nothing yet
    } else {
      filterBySearchTerm({
        searchTerm: urlSearchString,
        forTaxonomy: urlShowFrom,
      }).then((result) => {
        // convert object like {kingdom: "Animalia", phylum: "Chordata", ...} to array [["kingdom", "Animalia"], ["phylum", "Chordata"], ...]
        // we start with all taxonomies, so the order is correct, then filter out the ones that are not in the result
        // and then convert to array
        const resultAsArray = specificityList
          .filter((spec) => Object.keys(result).includes(spec))
          .map((spec) => [spec, result[spec]]);
        setCarouselItems(resultAsArray);
      });
    }
  }, [urlSearchString, urlShowFrom, filterBySearchTerm]);

  useEffect(() => {
    if (carouselItems) {
      const currentCarouselItem =
        carouselItems[carouselItems.length - 1 + urlSelectedIndex];
      if (currentCarouselItem) {
        filterBySearchTerm({
          searchTerm: currentCarouselItem[1],
          forTaxonomy: currentCarouselItem[0],
        });

        const hiddenSpecificities = specificityList.filter(
          (spec) => !isEqualOrMoreSpecific(spec, currentCarouselItem[0])
        );

        const percentageColumns =
          currentCarouselItem[0] === "species"
            ? [
              "allPercentageCoverage",
              "arisePercentageCoverage",
              "otherPercentageCoverage",
              "speciesTotal",
            ]
            : [
              "allPercentageCoverage",
              "arisePercentageCoverage",
              "otherPercentageCoverage",
            ];

        let combined = [...hiddenSpecificities, ...percentageColumns];

        if (groupBy !== "species") {
          combined = [...combined, "speciesStatus", "collected"];
        } else {
          combined = [...combined, "speciesTotal", "speciesWithBarcode"];
        }

        if (isMobile) {
          combined = columnKeys.filter((col) => !mobileColumns.includes(col));
        }

        setHideColumns(combined);
        setCurrentTaxon(currentCarouselItem);
      }
    }
  }, [
    urlSelectedIndex,
    filterBySearchTerm,
    carouselItems,
    setHideColumns,
    groupBy,
    isMobile,
  ]);

  const toggleFilters = function () {
    var filters = document.getElementById("filters-sidebar");
    filters.focus();
    filters?.classList.toggle("hide");
    filters?.classList.toggle("show");
  };

  const updateCurrentIndex = useCallback(
    (index) => {
      searchParams.set("selected", index);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  //percentages for chart
  const getPercentages = (group) => {
    const totalSpecies = group.length;
    const totalAriseSpecies = group.filter((i) => i.ariseBarcodes+i.collected > 0).length;
    const totalOtherSpecies = group.filter((i) => i.otherBarcodes > 0).length;
      const totalAllSpecies = group.filter((i) => i.allBarcodes + i.collected > 0).length;
    const totalSharedSpecies = group.filter(
        (i) => i.ariseBarcodes + i.collected > 0 && i.otherBarcodes > 0,
    ).length;
    const uniqueAriseSpecies = group.filter(
        (i) => i.ariseBarcodes + i.collected > 0 && i.otherBarcodes === 0,
    ).length;
    if (totalSpecies === 0) {
      return {
        arise: 0,
        other: 0,
        total: 0,
        shared: 0,
        uniqueArisePercent: 0,
        totalSpecies: 0,
        totalAriseSpecies: 0,
        totalOtherSpecies: 0,
        totalSharedSpecies: 0,
      };
    }
    return {
      arise: Math.round((totalAriseSpecies / totalSpecies) * 100),
      other: Math.round((totalOtherSpecies / totalSpecies) * 100),
      total: Math.round((totalAllSpecies / totalSpecies) * 100),
      shared: Math.round((totalSharedSpecies / totalSpecies) * 100),
      uniqueArisePercent: Math.round((uniqueAriseSpecies / totalSpecies) * 100),
      totalSpecies,
      totalAriseSpecies,
      totalOtherSpecies,
      totalSharedSpecies,
    };
  };

  const chartSpecies =
    table?.getFilteredRowModel()?.rows?.map((row) => row.original) ?? [];

  const chartData =
    currentTaxon && chartSpecies.length > 0
      ? {
        ...getPercentages(chartSpecies),
        taxon: currentTaxon[1],
        rank: currentTaxon[0].toLowerCase(),
      }
      : null;

  return (
    <>
      {!urlSearchString && !urlShowFrom ? (
        <div className="tl-home-main">
          <div className="tl-home-left">
            <h1>Identify all species</h1>
            <p className="subtitle-1" dangerouslySetInnerHTML={{ __html: theme.welcome }} />
            <p className="subtitle-2">This information can be used to easily identify gaps in the Dutch DNA species reference barcode database.</p>
            {!isMobile && (
              <KingdomsCharts data={kingdomData} loading={loading} />
            )}
          </div>
          <TargetlistSearch />
          {isMobile && (
            <MobileKingdomCharts data={kingdomData} loading={loading}></MobileKingdomCharts>
          )}
        </div>
      ) : (
        <>
          <Breadcrumbs
            currentIndex={urlSelectedIndex}
            crumbItems={carouselItems.map((item, index) => ({ 
              text: item[1],
              action: () => updateCurrentIndex(-carouselItems.length + index + 1),
            }))}
          />

          {/* Desktop or Mobile Non-Species: show Grid, Header, Chart */}
          {(!isMobile || (isMobile && currentTaxon?.[0] !== "species")) && (
            <div className="grid-chart-container">
              <div className="grid-container">
                <TargetlistGridHeader
                  toggleFilters={toggleFilters}
                  onGroupByChange={setGroupBy}
                />
                <TargetlistGrid hideColumns={hideColumns} />
              </div>

              {/* Show TableChart only if NOT mobile */}
              {!isMobile && (
                <div className="table-chart-container">
                  <div className="table-chart-top">
                    <small className="mb-0">
                      {numberOfItems > 1
                        ? `${numberOfItems} results found`
                        : `${numberOfItems} result found`}
                    </small>
                  </div>
                  {chartData ? (
                    <TableChart
                      key={`${chartData.taxon}-${chartData.rank}-${chartData.totalSpecies}`}
                      data={chartData}
                    />
                  ) : (
                    <p className="text-small gray">Loading chart...</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Mobile + species level: show SpeciesIdCard only */}
          {isMobile && currentTaxon?.[0] === "species" && (
            <>
              {chartSpecies.map((item, index) => (
                <SpeciesIdCard
                  key={index}
                  item={[item.species, item.species]}
                  kingdom={item.kingdom}
                  occStatus={item.speciesOccurrenceStatus}
                  wanted={item.wanted}
                  allBarcodes={item.allBarcodes}
                  ariseBarcodes={item.ariseBarcodes}
                  otherBarcodes={item.otherBarcodes}
                  collected={item.collected}
                  locality={item.speciesLocality?.[0] || ""}
                />
              ))}
            </>
          )}
        </>
      )}
    </>
  );

}